/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback } from "react";
import styled from "styled-components";
import { isToday, isBefore, startOfDay, format } from "date-fns";
import { useClickableA11y } from "../../hooks/a11y/useClickableA11y";

const StyledCalendarDay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;

  ${({ theme }) => theme.containerMedia.sm} {
    padding: 0.375rem;
  }
`;

const CalendarDayNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.875);
  line-height: 1.43;
  color: rgba(${({ theme }) => theme.colors.text.rgba});
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  &.today {
    font-weight: 600;
    color: rgba(${({ theme }) => theme.colors.accent.rgba});
  }

  &.selected {
    font-weight: 600;
    background: rgba(${({ theme }) => theme.colors.accent.rgba});
    color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
  }

  @media (hover: hover) {
    &:hover {
      font-weight: 600;
      background: rgba(${({ theme }) => theme.colors.accent.rgba});
      color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
    }
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }
`;

const CalendarDay = ({ day, date, minDate, maxDate, onClick }) => {
  const isTodayDay = isToday(day.date);
  const isDisabled = day.is_day_off || isBefore(day.date, startOfDay(new Date()));
  const dayIso = format(day.date, "yyyy-MM-dd");
  const isSelected = date === dayIso;
  // Days outside the membership coverage window can't be booked.
  const isOutsideCoverage = (minDate && dayIso < minDate) || (maxDate && dayIso > maxDate);

  const handleDayClick = useCallback(() => {
    onClick(day.date);
  }, [onClick, day.date]);

  const a11yProps = useClickableA11y({
    onClick: handleDayClick,
    disabled: isDisabled || isOutsideCoverage,
    selected: isSelected,
  });

  return (
    <StyledCalendarDay>
      <CalendarDayNumber
        className={[
          isTodayDay && "today",
          (isDisabled || isOutsideCoverage) && "disabled",
          isSelected && "selected",
        ].filter(Boolean).join(" ")}
        onClick={handleDayClick}
        {...a11yProps}
        aria-label={format(day.date, "EEEE, MMMM d, yyyy")}
        aria-current={isTodayDay ? "date" : undefined}
      >
        {format(day.date, "d")}
      </CalendarDayNumber>
    </StyledCalendarDay>
  );
};

export default CalendarDay;
